import { assertUnreachable, isDefined } from '@sgme/fp';
import { Icon } from '@sgme/ui';
import type { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';

import { SgTooltip } from '@/components/sg-design-system/components/feedback/SgTooltip';
import { type TradePermission, useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { getIsStreamingConnected } from '@/store/streaming/slice.ts';
import type { IntlMessageId } from '@/types/intl.ts';

type Props = PropsWithChildren<{
  placement: 'left' | 'right';
  isPriceStale?: boolean;
}>;

export const ForbiddenExecutionTooltip = ({ children, placement, isPriceStale = false }: Props) => {
  const userContextValue = useUserContext();

  // explicit reason why trading is forbidden (if forbidden)
  const tradePermission = isDefined(userContextValue)
    ? userContextValue.tradePermission
    : 'disallowedByUserPermissions'; // safe default until userContext is ready;

  const isStreamingConnected = useAppSelector(getIsStreamingConnected);

  if (tradePermission === 'allowed' && isStreamingConnected && !isPriceStale) {
    return children;
  }

  return (
    <SgTooltip mode="hover" backgroundColor="black" color="white" placement={placement} arrow>
      <SgTooltip.Trigger>{children}</SgTooltip.Trigger>
      <SgTooltip.Content className="p-2 text-center">
        <FormattedMessage id="cantTrade.tooltip.tradingDisabled" />
        <br />
        <FormattedMessage
          id={getForbiddenExecutionReasonMessageId(tradePermission, isStreamingConnected)}
          values={{ lockIcon: <Icon icon="lock" size="sm" className="text-danger" /> }}
        />
      </SgTooltip.Content>
    </SgTooltip>
  );
};

function getForbiddenExecutionReasonMessageId(
  tradePermission: TradePermission,
  isStreamingConnected: boolean,
): IntlMessageId {
  switch (tradePermission) {
    case 'disallowedByUserPermissions':
      return 'cantTrade.tooltip.userCantTrade';

    case 'disallowedByWorkspace':
      return 'cantTrade.tooltip.lockedWorkspace';

    case 'allowed':
      return isStreamingConnected ? 'cantTrade.tooltip.streamingDegraded' : 'cantTrade.tooltip.streamingUnavailable';

    default:
      return assertUnreachable(tradePermission, `Unknown trade permission: ${tradePermission}`);
  }
}
